import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChartBar, DownloadSimple, EnvelopeSimple, SpinnerGap, TrendUp, TrendDown, Minus, TelegramLogo } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const QuarterlyReport = () => {
  const [quarters, setQuarters] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/export/available-quarters`, { withCredentials: true });
        setQuarters(data);
        if (data.length > 0) setSelectedKey(`${data[0].financial_year}|${data[0].quarter}`);
      } catch (err) { console.error(err.message); }
      finally { setFetching(false); }
    };
    fetch();
  }, []);

  const selected = quarters.find(q => `${q.financial_year}|${q.quarter}` === selectedKey);

  const handleDownload = async () => {
    if (!selected) return;
    setLoading(true);
    toast.info('Generating quarterly report with AI commentary...');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/quarterly-report`, {
        params: { fy: selected.financial_year, quarter: selected.quarter },
        withCredentials: true,
        responseType: 'blob',
        timeout: 60000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selected.quarter_label.replace(/[\s()]/g, '_')}_report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Quarterly report downloaded');
    } catch (err) { console.error(err.message);
      toast.error('Failed to generate quarterly report');
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async () => {
    if (!selected) return;
    setEmailing(true);
    toast.info('Generating & emailing quarterly report to your team...');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/export/quarterly-report/email`,
        { fy: selected.financial_year, quarter: selected.quarter },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success(data.message || 'Quarterly report emailed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email report');
    } finally {
      setEmailing(false);
    }
  };

  const handleTelegram = async () => {
    if (!selected) return;
    setSendingTelegram(true);
    toast.info('Generating & sending quarterly report via Telegram...');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/send-report`,
        { report_type: 'quarterly', fy: selected.financial_year, quarter: selected.quarter },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success(data.message || 'Quarterly report sent via Telegram');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send via Telegram');
    } finally {
      setSendingTelegram(false);
    }
  };

  // Find previous quarter for comparison display
  const prevQuarter = (() => {
    if (!selected) return null;
    const pq = selected.quarter === 1 ? 4 : selected.quarter - 1;
    const pfy = selected.quarter === 1
      ? `FY ${parseInt(selected.financial_year.split(' ')[1].split('-')[0]) - 1}-${selected.financial_year.split(' ')[1].split('-')[0].slice(2)}`
      : selected.financial_year;
    return quarters.find(q => q.financial_year === pfy && q.quarter === pq);
  })();

  const qoqGrowth = selected && prevQuarter && prevQuarter.total_revenue > 0
    ? ((selected.total_revenue - prevQuarter.total_revenue) / prevQuarter.total_revenue * 100).toFixed(1)
    : null;

  if (fetching || quarters.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="quarterly-report-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-sm bg-accent/10 flex items-center justify-center">
          <ChartBar size={20} weight="fill" className="text-accent" />
        </div>
        <div>
          <div className="text-lg font-semibold tracking-tight">Quarterly Review Report</div>
          <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">QoQ COMPARISON WITH AI COMMENTARY</div>
        </div>
      </div>

      <div className="flex items-end gap-4 flex-wrap">
        <div className="space-y-1.5">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Quarter</div>
          <Select value={selectedKey} onValueChange={setSelectedKey}>
            <SelectTrigger className="w-[260px] rounded-sm" data-testid="quarter-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {quarters.map(q => (
                <SelectItem key={`${q.financial_year}|${q.quarter}`} value={`${q.financial_year}|${q.quarter}`}>
                  {q.quarter_label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selected && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">₹{selected.total_revenue.toLocaleString()}</span>
            <span className="text-muted-foreground">{selected.total_transactions.toLocaleString()} txns</span>
            {qoqGrowth !== null && (
              <span className={`font-semibold flex items-center gap-1 ${parseFloat(qoqGrowth) > 0 ? 'text-accent' : parseFloat(qoqGrowth) < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {parseFloat(qoqGrowth) > 0 ? <TrendUp size={14} /> : parseFloat(qoqGrowth) < 0 ? <TrendDown size={14} /> : <Minus size={14} />}
                {parseFloat(qoqGrowth) > 0 ? '+' : ''}{qoqGrowth}% QoQ
              </span>
            )}
          </div>
        )}

        <Button
          onClick={handleDownload}
          disabled={loading || emailing}
          className="rounded-sm"
          data-testid="download-quarterly-report-button"
        >
          {loading ? (
            <><SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />Generating...</>
          ) : (
            <><DownloadSimple size={16} weight="bold" className="mr-2" />Download PDF</>
          )}
        </Button>

        <Button
          onClick={handleEmail}
          disabled={emailing || loading}
          variant="outline"
          className="rounded-sm"
          data-testid="email-quarterly-report-button"
        >
          {emailing ? (
            <><SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />Sending...</>
          ) : (
            <><EnvelopeSimple size={16} weight="bold" className="mr-2" />Email to Team</>
          )}
        </Button>

        <Button
          onClick={handleTelegram}
          disabled={sendingTelegram || loading || emailing}
          variant="outline"
          className="rounded-sm text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/20"
          data-testid="telegram-quarterly-report-button"
        >
          {sendingTelegram ? (
            <><SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />Sending...</>
          ) : (
            <><TelegramLogo size={16} weight="fill" className="mr-2" />Telegram</>
          )}
        </Button>
      </div>

      <div className="mt-4 text-xs text-muted-foreground space-y-1">
        <div className="flex items-center gap-1.5">
          <ChartBar size={12} weight="regular" />
          Includes QoQ comparison, client growth/decline analysis, monthly breakdown, and AI commentary
        </div>
        <div className="flex items-center gap-1.5">
          <EnvelopeSimple size={12} weight="regular" />
          Auto-sent at end of each quarter: Jun 30, Sep 30, Dec 31, Mar 31
        </div>
      </div>
    </div>
  );
};

export default QuarterlyReport;
